import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

/** What the hook needs of a client: the number it is addressed by, and the name to filter on. */
interface RoutableClient {
    ClNr: number;
    Nume: string;
}

/**
 * Select the client another page navigated here with, once the client list has arrived.
 *
 * The id comes either from `?clientId=` or from the navigation state, and is consumed: the
 * parameter and the state are cleared with a replacing navigation so a reload, or a back
 * button, does not re-select a client the user has since moved away from. Nothing happens
 * while a client is already selected, or while the id names a client the list does not hold.
 *
 * `onSelect` is read through a ref so that passing an inline callback -- which every caller
 * does -- cannot turn this into an effect that runs on every render.
 */
export const useRouteSelectedClient = <T extends RoutableClient>(
    clients: T[],
    hasSelection: boolean,
    onSelect: (client: T) => void,
) => {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();

    const onSelectRef = useRef(onSelect);
    useEffect(() => {
        onSelectRef.current = onSelect;
    });

    useEffect(() => {
        if (hasSelection) {
            return;
        }

        const clientIdParam = searchParams.get('clientId');
        const stateClientId = (location.state as { clientId?: number })?.clientId;
        const targetClientId = clientIdParam ? Number(clientIdParam) : stateClientId;
        const client = targetClientId
            ? clients.find(candidate => candidate.ClNr === targetClientId)
            : undefined;

        if (!client) {
            return;
        }

        onSelectRef.current(client);

        const remaining = new URLSearchParams(searchParams);
        remaining.delete('clientId');
        const queryString = remaining.toString();
        // The suffix is built first rather than inline: a template literal nested inside
        // another is hard to read and Sonar rejects it.
        const suffix = queryString ? `?${queryString}` : '';
        navigate(`/contracts${suffix}`, { replace: true, state: {} });
    }, [clients, searchParams, location.state, hasSelection, navigate]);
};

export default useRouteSelectedClient;
